// packages
import dayjs from 'dayjs';
import { format, getDay, parse, startOfWeek } from 'date-fns';
import { nl } from 'date-fns/locale/nl';

import {
  Calendar as ReactBigCalender,
  dateFnsLocalizer,
  DateLocalizer,
  Event as ReactBigCalendarEvent,
  Formats
} from 'react-big-calendar';

// styles
import { CalendarContainer } from './Calendar.styles';

// types
import type { CalendarProps, CalendarResource } from './Calendar.types';

// utils
import { JSX, memo, useMemo } from 'react';
import { CalendarDnDKitDefaults } from 'components/mrp/mrp.consts';
import { planningPeriodToBigCalendarPeriod } from './Calendar.utils';
import { isEqual } from 'lodash';

// components
import CalendarColumnHeader from './components/CalendarColumnHeader/CalendarColumnHeader';

const localizer: DateLocalizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales: {
    nl
  }
});

const formats: Formats = {
  dateFormat: 'dd',
  timeGutterFormat: 'HH:mm',
  dayFormat: (date, culture, localizer) =>
    localizer!.format(date, 'EEEE dd MMM', culture)
};

const Calendar = <AppointmentEvent extends ReactBigCalendarEvent>({
  className,
  components,
  events = [],
  backgroundEvents = [],
  onDoubleClick,
  onSelectEvent,
  onNavigate,
  onSelectSlot,
  ref,
  selectable = true,
  periodType = 'week',
  showToolBar = false,
  startDate = new Date(),
  scrollToTime,
  timeSlotMinMax = [
    dayjs().startOf('day').toDate(),
    dayjs().endOf('day').toDate()
  ],
  timeStep = 30,
  eventPropGetter,
  resources,
  availabilityColumnWidths = []
}: CalendarProps<AppointmentEvent>) => {
  /**
   * @description Note that we must keep the 'views' and 'view' prop of React Big Calendar in sync,
   * as it is ordinarily not used as a controlled component and will throw an arror if either 'view' or 'views' are out of sync
   */
  const activePeriod = useMemo(
    () => planningPeriodToBigCalendarPeriod(periodType),
    [periodType]
  );

  const views = useMemo(() => {
    const periods = {
      week: false,
      work_week: false,
      day: false
    };

    return { ...periods, [activePeriod]: true };
  }, [periodType]);

  return (
    <CalendarContainer ref={ref} columnWidths={availabilityColumnWidths}>
      <ReactBigCalender<AppointmentEvent, CalendarResource>
        {...CalendarDnDKitDefaults}
        {...(!!resources && {
          resources,
          resourceGroupingLayout: true,
          resourceIdAccessor: ({ resourceId }) => resourceId,
          resourceTitleAccessor: ({ resourceTitle }) => resourceTitle
        })}
        className={className}
        components={{
          work_week: { header: CalendarColumnHeader },
          week: { header: CalendarColumnHeader },
          day: { header: CalendarColumnHeader },
          ...components
        }}
        culture="nl"
        date={new Date(startDate)}
        dayLayoutAlgorithm="no-overlap"
        showMultiDayTimes
        events={events}
        backgroundEvents={backgroundEvents}
        formats={formats}
        localizer={localizer}
        scrollToTime={scrollToTime}
        min={timeSlotMinMax[0]}
        max={timeSlotMinMax[1]}
        onDoubleClickEvent={onDoubleClick}
        onSelectEvent={onSelectEvent}
        onNavigate={onNavigate}
        onSelectSlot={onSelectSlot}
        selectable={selectable}
        startAccessor="start"
        tooltipAccessor={() => ''} // disable default ReactBigCalender tooltip
        timeslots={1}
        step={timeStep}
        endAccessor="end"
        toolbar={showToolBar}
        view={activePeriod}
        views={views}
        eventPropGetter={eventPropGetter}
      />
    </CalendarContainer>
  );
};

// This method reduces the entire object to only those relevant
// for memoization. Add any new properties that require the
// calendar to re-render here.
const reduceToMemoProps = (p: CalendarProps<any>) => {
  const keys: ReadonlyArray<Extract<keyof CalendarProps<any>, string>> = [
    'endDate',
    'events',
    'backgroundEvents',
    'selectable',
    'start',
    'periodType',
    'showToolBar',
    'startDate',
    'timeSlotMinMax',
    'timeStep',
    'availabilityColumnWidths',
    'className'
  ];

  return keys.reduce((acc, key) => {
    acc[key] = p[key];
    return acc;
  }, {});
};
const Memoed = memo(
  Calendar as (p: CalendarProps<any>) => JSX.Element,
  (prev, next) => {
    return isEqual(reduceToMemoProps(prev), reduceToMemoProps(next));
  }
);

// Explicit version is necessary because memoization doesn't allow
// passing generic.
export const MemoCalendar = <AppointmentEvent extends ReactBigCalendarEvent>(
  props: CalendarProps<AppointmentEvent>
) => {
  return <Memoed {...props} />;
};

// Only use this if you want a completely unmemoized version of the
// calendar.
export default Calendar;
